import type { Student } from "../types/models";

/**
 * Generate initials from a student's name
 */
export function generateInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        const first = parts[0]?.charAt(0) ?? '';
        const last = parts[parts.length - 1]?.charAt(0) ?? '';
        return (first + last).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
}

/**
 * Get avatar display info for a student
 */
export function getAvatarDisplay(student: Student): { type: 'image' | 'initials'; value: string } {
    if (student.avatarUrl) {
        return { type: 'image', value: student.avatarUrl };
    }
    return { type: 'initials', value: generateInitials(student.name) };
}

/**
 * Generate a consistent color based on a string (e.g., student name or ID)
 */
export function stringToColor(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const colors = [
        '#4f46e5', // Indigo
        '#7c3aed', // Violet
        '#db2777', // Pink
        '#dc2626', // Red
        '#ea580c', // Orange
        '#ca8a04', // Yellow
        '#16a34a', // Green
        '#0891b2', // Cyan
        '#2563eb', // Blue
        '#9333ea', // Purple
    ];

    return colors[Math.abs(hash) % colors.length] ?? colors[0]!;
}

/**
 * Process an image file and return a data URL
 * Resizes to max 100x100 to save storage space
 */
export async function processImageFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
            reject(new Error('File must be an image'));
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Resize to max 100x100
                const maxSize = 100;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxSize) {
                        height = height * (maxSize / width);
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width = width * (maxSize / height);
                        height = maxSize;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Could not get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                // Convert to JPEG for smaller size
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                resolve(dataUrl);
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

/**
 * Create an avatar element for a student
 */
export function createAvatarElement(student: Student, size: number = 32): HTMLElement {
    const avatar = document.createElement('div');
    avatar.className = 'student-avatar';
    avatar.style.cssText = `
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${Math.round(size * 0.4)}px;
        font-weight: 600;
        flex-shrink: 0;
    `;

    const display = getAvatarDisplay(student);

    if (display.type === 'image') {
        const img = document.createElement('img');
        img.src = display.value;
        img.alt = student.name;
        img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
        img.onerror = () => {
            // Fallback to initials if image fails to load
            avatar.innerHTML = '';
            avatar.textContent = generateInitials(student.name);
            avatar.style.backgroundColor = stringToColor(student.id);
            avatar.style.color = 'white';
        };
        avatar.appendChild(img);
    } else {
        avatar.textContent = display.value;
        avatar.style.backgroundColor = stringToColor(student.id);
        avatar.style.color = 'white';
    }

    return avatar;
}

/**
 * Estimate storage usage for avatars
 */
export function estimateAvatarStorageUsage(): { used: number; percentage: number } {
    try {
        const stateStr = localStorage.getItem('GG:v1:state');
        if (!stateStr) return { used: 0, percentage: 0 };

        // Count avatar data URLs in the state
        const avatarMatches = stateStr.match(/avatarUrl":"data:[^"]+/g);
        const avatarBytes = avatarMatches?.reduce((sum, match) => sum + match.length, 0) ?? 0;

        // localStorage limit is typically 5-10MB
        const estimatedLimit = 5 * 1024 * 1024;
        const percentage = (avatarBytes / estimatedLimit) * 100;

        return { used: avatarBytes, percentage: Math.round(percentage * 10) / 10 };
    } catch {
        return { used: 0, percentage: 0 };
    }
}
